import React, { useEffect } from "react";
import { connect, useDispatch } from "react-redux";
import SinglePreviewCard from "./SinglePreviewCard";
import { couponSetFieldPrefix, getCouponSetMedium } from "containers/engagement/campaignStreamline/CreateCampaignCouponPageConfig";
import { CAMPAIGN_COUPON_PAGE_ANCHOR_KEY, COUPON_CODE_TYPE, COUPON_SET_MEDIUM, COUPON_SET_TYPES, DISPLAY_COUPON_CODE_TYPE, DISPLAY_COUPON_FORMATS, DISPLAY_COUPON_REDEMPTION_METHOD, DISPLAY_COUPON_SET_TYPES, WEEKDAY } from "config/CustomEnums";
import { getReadablePeriod } from "utils/TimeFormatUtil";
import GroupedStoresDisplaySection from "./GroupedStoresDisplaySection";
import { createAction } from "utils";
import { CampaignStreamlineGenerateCouponSection } from "./GenerateCouponSection";

const CouponSetCard = (couponTemplateData, allGroupedCouponStores, isWithinCreationFlow) => {
  let couponTemplateCodeSections = []
  const mediumType = couponTemplateData?.mediumType
  const isDifferentCodeForOnlineOffline = couponTemplateData?.isDifferentCodeForOnlineOffline

  const offlineCodePrefix = "readableOffline"
  const onlineCodePrefix = "readableOnline"
  const onlineOfflinePrefix = "readableOnlineAndOffline"

  const couponSourceFieldName = `CouponSetType`;
  const couponRedemptionMethodFieldName = `RedemptionMethod`;
  const couponCodeFormatFieldName = `Formats`;
  const couponCouponCodeTypeFieldName = `CouponCodeType`;
  const couponCodeFieldName = `GenericCode`;
  
  const getCouponCodeSection = (title, prefix, displayShowCopyCode=false) => {
    let sectionContent = [
      {
        title: "Coupon redemption method",
        field: `${prefix}${couponRedemptionMethodFieldName}`,
        isMandatory: true,
        anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.couponProperties.sectionContent.coupon_properties_section_key,
      },
      {
        title: "Coupon source",
        field: `${prefix}${couponSourceFieldName}`,
        isMandatory: true,
        anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.couponProperties.sectionContent.coupon_properties_section_key,
      },
    ]
    if (couponTemplateData?.[`${prefix}${couponSourceFieldName}`] === DISPLAY_COUPON_SET_TYPES?.[COUPON_SET_TYPES.IMPORT_EXISTING_COUPON]) {
      sectionContent = [
        ...sectionContent,
        {
          title: "Coupon code setting",
          field: `${prefix}${couponCodeFormatFieldName}`,
          isMandatory: true,
          anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.couponProperties.sectionContent.coupon_properties_section_key,
        },
        ...couponTemplateData?.[`${prefix}${couponCodeFormatFieldName}`] === DISPLAY_COUPON_FORMATS.COUPON_BAR_CODE ? 
        [
          {
            title: "Barcode format",
            field: `${offlineCodePrefix}BarcodeFormat`,
            anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.couponProperties.sectionContent.coupon_properties_section_key,
          }
        ] : [],
        {
          title: "Generic code or Unique code",
          field: `${prefix}${couponCouponCodeTypeFieldName}`,
          isMandatory: true,
          anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.couponProperties.sectionContent.coupon_properties_section_key,
        }
      ]
      if (couponTemplateData?.[`${prefix}${couponCouponCodeTypeFieldName}`] === DISPLAY_COUPON_CODE_TYPE?.[COUPON_CODE_TYPE.GENERIC]) {
        sectionContent.push({
          title: "Generic code",
          field: `${prefix}${couponCodeFieldName}`,
          isMandatory: true,
          anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.couponProperties.sectionContent.coupon_properties_section_key,
        })
      }
    }
    if (displayShowCopyCode
      && couponTemplateData?.[`${prefix}${couponRedemptionMethodFieldName}`]
      === DISPLAY_COUPON_REDEMPTION_METHOD.POS_UPDATED_CODE) {
      sectionContent.push({
        title: "Show copy code button",
        field: `offlineShowCopiableString`,
        isMandatory: true,
        isBoolean: true,
        anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.couponProperties.sectionContent.coupon_properties_section_key,
      })
    }
    return {
      step: 3,
      sectionTitle: title,
      anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.couponProperties.sectionKey,
      sectionContent,
    }
  }

  if (mediumType === COUPON_SET_MEDIUM.ONLIEN_OR_OFFLINE && !isDifferentCodeForOnlineOffline) {
    couponTemplateCodeSections.push(getCouponCodeSection("Both online and offline coupon code setting", onlineOfflinePrefix))
  } else if (mediumType === COUPON_SET_MEDIUM.ONLIEN_OR_OFFLINE) {
    couponTemplateCodeSections.push(getCouponCodeSection("Online coupon code setting", onlineCodePrefix))
    couponTemplateCodeSections.push(getCouponCodeSection("Offline coupon code setting", offlineCodePrefix, true))
  } else if (mediumType === COUPON_SET_MEDIUM.ONLINE) {
    couponTemplateCodeSections.push(getCouponCodeSection("Online coupon code setting", onlineCodePrefix))
  } else if (mediumType === COUPON_SET_MEDIUM.OFFLINE) {
    couponTemplateCodeSections.push(getCouponCodeSection("Offline coupon code setting", offlineCodePrefix, true))
  }


  return [
    ...isWithinCreationFlow ? [
    {
      step: 3,
      anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.sameAsCampaignSection.sectionKey,
      sectionContent: [
        {
          haveTranslation: false,
          title: "Follow campaign content",
          field: 'couponSetContentSameAsCampaign',
          isMandatory: true,
          isBoolean: true,
          anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.sameAsCampaignSection.sectionContent.same_as_campaign_key,
        },
      ]
      }] : [],
    {
      step: 3,
      sectionTitle: "General",
      anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.couponGeneral.sectionKey,
      sectionContent: [
        {
          haveTranslation: true,
          isMandatory: true,
          title: "name",
          field: 'name',
          anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.couponGeneral.sectionContent.general_name_key,
        },
      {
        haveTranslation: true,
        isRichText: true,
        title: "short_description_optional",
        field: 'shortDescription',
        anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.couponGeneral.sectionContent.short_description_optional_key,
      },
      ],
    },
    {
      step: 3,
      sectionTitle: "Key visual",
      anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.couponKeyVisual.sectionKey,
      sectionContent: [
        {
          haveTranslation: true,
          isMandatory: true,
          isImage: true,
          title: "cover_photo",
          field: 'coverPhoto',
          anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.couponKeyVisual.sectionContent.cover_photo_upload_key,
        },
        {
          isMandatory: true,
          isBoolean: true,
          title: "Apply to all language",
          field: `applyCoverPhotoToAllLanguage`,
          anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.couponKeyVisual.sectionContent.cover_photo_upload_key,
        },
        {
          haveTranslation: true,
          isImage: true,
          title: "other_photos",
          field: 'detailPhotos',
          anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.couponKeyVisual.sectionContent.other_photo_upload_key,
        },
        {
          isMandatory: true,
          isBoolean: true,
          title: "Apply to all language",
          field: `applyDetailPhotosToAllLanguage`,
          anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.couponKeyVisual.sectionContent.other_photo_upload_key,
        },
      ],
    },
    {
      step: 3,
      sectionTitle: "Instruction",
      anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.couponInstruction.sectionKey,
      sectionContent: [
       {
         haveTranslation: true,
         title: "instruction_section_title",
          field: 'instructionSectionTitle',
          anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.couponInstruction.sectionContent.instruction_section_key,
       },
       {
         haveTranslation: true,
         isRichText: true,
         title: "instruction_section_content",
         field: 'instructionSectionContent',
         anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.couponInstruction.sectionContent.instruction_section_key,
       },
      ]
    },
    {
      step: 3,
      sectionTitle: "Detail",
      anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.couponDetail.sectionKey,
      sectionContent: [
        {
          haveTranslation: true,
          title: "detail_section_title",
          field: 'detailSectionTitle',
          anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.couponDetail.sectionContent.detail_section_key,
        },
        {
          haveTranslation: true,
          isRichText: true,
          title: "detail_section_content",
          field: 'detailSectionContent',
          anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.couponDetail.sectionContent.detail_section_key,
        },
      ],
    },
    {
      step: 3,
      sectionTitle: "Coupon valid period",
      anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.validPeriod.sectionKey,
      sectionContent: [
        {
          title: "Blackout period (by period)",
          field: 'readableBlackoutPeriod',
          anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.validPeriod.sectionContent.valid_period_type_key,
        },
        {
          title: "Blackout period (by weekday)",
          field: 'readableBlackoutWeekday',
          anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.validPeriod.sectionContent.valid_period_type_key,
        },
      ],
    },
    {
      step: 3,
      sectionTitle: "Coupon applicable store",
      anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.applicableStores.sectionKey,
      sectionContent: [
        {
          isMandatory: true,
          title: `Summary of selected stores (${couponTemplateData?.selectedStores?.length || 0})`,
          anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.applicableStores.sectionContent.store_selector_key,
          customRender: <GroupedStoresDisplaySection
            hideSummaryTitle={true}
            isPreview={true}
            allSelectedStores={couponTemplateData?.selectedStores}
            allGroupedCouponStores={allGroupedCouponStores}
          />
      },
        {
          title: "Brand to be displayed on front end",
          field: `displayOnFrontendBrands`,
          anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.applicableStores.sectionContent.related_brand_optional_key,
          customRender: (
            <div class="one-group display-brand-group">
              <ul>
                {couponTemplateData?.displayOnFrontendBrands?.map((item) => (
                  <li>
                    <div class="dot"></div>
                    {`[ID: ${item?.value.pk}] ${item?.value.name}`}
                  </li>
                )) || "-"}
              </ul>
            </div>
          )
        },
    ],
    },
    {
      step: 3,
      sectionTitle: "Code setting",
      anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.couponProperties.sectionKey,
      sectionContent: [
        {
          isMandatory: true,
          isBoolean: true,
          title: "Set different codes for online & offline stores",
          field: 'isDifferentCodeForOnlineOffline',
          anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.couponProperties.sectionContent.coupon_properties_section_key,
        },
      ],
    },
    ...couponTemplateCodeSections || [],
    ...isWithinCreationFlow ? [
      {
        step: 3,
        sectionTitle: "Import/Generate single coupons",
        anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.generateCoupons.sectionKey,
        sectionContent: [
          {
            isMandatory: false,
            title: "Related brand",
            anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.generateCoupons.sectionContent.generate_coupons_key,
            customRender: (<CampaignStreamlineGenerateCouponSection couponSetFieldPrefix={couponSetFieldPrefix} disabled={true} hideSectionHeader />)
          },
        ],
      },
    ] : [],
    {
      step: 3,
      sectionTitle: "Additional data",
      anchorKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.additionalData.sectionKey,
      sectionContent: couponTemplateData?.additionalData?.map((data, index)=> (
        {
          isMandatory: false,
          title: `Fields #${index + 1}`,
          anchorKey: `${CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.generateCoupons.sectionContent.generate_coupons_key}_${index}`,
          customRender: (
            <div>
              <div>{`Title: ${data?.title || '-'}`}</div>
              <div>{`Value: ${data?.value || '-'}`}</div>
            </div>
          )
        }
      ))
    }
  ]
}

const CouponSetPreviewCard = ({
  language,
  couponTemplateData,
  isWithinCreationFlow=true,
  hideEditButton=false,
  errors,
  allGroupedCouponStores,
}) => {
  const dispatch = useDispatch();
  const couponSetDataForDisplay = {
    ...couponTemplateData,
    mediumType: getCouponSetMedium(couponTemplateData?.selectedStores || []),
    linkedBrand: couponTemplateData?.linkedBrand?.name,
    readableActivePeriod: getReadablePeriod(
      couponTemplateData.activeStartDate,
      couponTemplateData.activeEndDate,
      couponTemplateData.isAlwaysActivePeriod,
    ),
    readableBlackoutPeriod: couponTemplateData.validPeriodValues?.map((item) =>
      getReadablePeriod(item.startDate, item.endDate, false),
    ),
    readableBlackoutWeekday: couponTemplateData.validWeekday?.map((item) => WEEKDAY[item]),
    // online and offline coupon code
    readableOnlineAndOfflineCouponSetType: DISPLAY_COUPON_SET_TYPES?.[couponTemplateData?.onlineAndOfflineCouponSetType]
      || couponTemplateData?.onlineAndOfflineCouponSetType,
    readableOnlineAndOfflineRedemptionMethod: DISPLAY_COUPON_REDEMPTION_METHOD?.[couponTemplateData?.onlineAndOfflineRedemptionMethod] 
    || couponTemplateData?.onlineAndOfflineRedemptionMethod,
    readableOnlineAndOfflineFormats: DISPLAY_COUPON_FORMATS?.[couponTemplateData?.onlineAndOfflineFormats]
      || couponTemplateData?.onlineAndOfflineFormats,
    readableOnlineAndOfflineCouponCodeType: DISPLAY_COUPON_CODE_TYPE?.[couponTemplateData?.onlineAndOfflineCouponCodeType]
      || couponTemplateData?.onlineAndOfflineCouponCodeType,
    // online coupon code
    readableOnlineCouponSetType: DISPLAY_COUPON_SET_TYPES?.[couponTemplateData?.onlineCouponSetType]
      || couponTemplateData?.onlineCouponSetType,
    readableOnlineRedemptionMethod: DISPLAY_COUPON_REDEMPTION_METHOD?.[couponTemplateData?.onlineRedemptionMethod]
      || couponTemplateData?.onlineRedemptionMethod,
    readableOnlineFormats: DISPLAY_COUPON_FORMATS?.[couponTemplateData?.onlineFormats]
      || couponTemplateData?.onlineFormats,
    readableOnlineCouponCodeType: DISPLAY_COUPON_CODE_TYPE?.[couponTemplateData?.onlineCouponCodeType]
      || couponTemplateData?.onlineCouponCodeType,
    // offline coupon code
    readableOfflineCouponSetType: DISPLAY_COUPON_SET_TYPES?.[couponTemplateData?.offlineCouponSetType]
      || couponTemplateData?.offlineCouponSetType,
    readableOfflineRedemptionMethod: DISPLAY_COUPON_REDEMPTION_METHOD?.[couponTemplateData?.offlineRedemptionMethod]
      || couponTemplateData?.offlineRedemptionMethod,
    readableOfflineFormats: DISPLAY_COUPON_FORMATS?.[couponTemplateData?.offlineFormats]
      || couponTemplateData?.offlineFormats,
    readableOfflineCouponCodeType: DISPLAY_COUPON_CODE_TYPE?.[couponTemplateData?.offlineCouponCodeType]
      || couponTemplateData?.offlineCouponCodeType,
    readableOfflineBarcodeFormat: couponTemplateData?.offlineBarcodeFormat?.label || couponTemplateData?.offlineBarcodeFormat?.value?.name,
  }
  useEffect(()=>{
    dispatch(createAction('storeModel/getAllCouponStores')({}))
  }, [])

  return (
    <SinglePreviewCard
      language={language}
      data={couponSetDataForDisplay}
      cardInfo={CouponSetCard(couponSetDataForDisplay, allGroupedCouponStores, isWithinCreationFlow)}
      errors={errors}
      hideEditButton={hideEditButton}
    />
  )
}

const mapPropsToState = (state) => ({
  errors: state.createCampaign.errorFields,
  allGroupedCouponStores: state.storeModel.couponStores,
});

export default  connect(mapPropsToState)(CouponSetPreviewCard);